import { chSupabase } from '../lib/supabase';
import type { ChPostPrint } from '../types';

// Until the stats SQL is applied the table doesn't exist: show no prints
// rather than breaking the page.
function isMissingTable(error: { code?: string } | null): boolean {
  return !!error && (error.code === '42P01' || error.code === 'PGRST205');
}

export const chPrintService = {
  async listByPost(postId: string): Promise<ChPostPrint[]> {
    const { data, error } = await chSupabase
      .from('ch_post_prints')
      .select('*')
      .eq('post_id', postId)
      .order('position')
      .order('created_at');

    if (isMissingTable(error)) return [];
    if (error) throw error;
    return (data || []) as ChPostPrint[];
  },

  async listByPosts(postIds: string[]): Promise<Record<string, ChPostPrint[]>> {
    if (postIds.length === 0) return {};
    const { data, error } = await chSupabase
      .from('ch_post_prints')
      .select('*')
      .in('post_id', postIds)
      .order('position')
      .order('created_at');

    if (isMissingTable(error)) return {};
    if (error) throw error;
    const byPost: Record<string, ChPostPrint[]> = {};
    for (const print of (data || []) as ChPostPrint[]) {
      if (!print.post_id) continue;
      (byPost[print.post_id] ||= []).push(print);
    }
    return byPost;
  },

  async upload(postId: string, projectId: string, file: File, position: number): Promise<ChPostPrint> {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `${projectId}/prints/${postId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: uploadError } = await chSupabase.storage
      .from('ch-media')
      .upload(path, file, { upsert: false });
    if (uploadError) throw uploadError;

    const { data: urlData } = chSupabase.storage.from('ch-media').getPublicUrl(path);
    const { data: { user } } = await chSupabase.auth.getUser();

    const { data, error } = await chSupabase
      .from('ch_post_prints')
      .insert({
        post_id: postId,
        url: urlData.publicUrl,
        position,
        created_by: user?.id ?? null,
      })
      .select()
      .single();

    if (isMissingTable(error)) {
      throw new Error('Falta aplicar o SQL das stats no Supabase (20260928140000_content_hub_stats_and_prints.sql)');
    }
    if (error) throw error;
    return data as ChPostPrint;
  },

  async updateCaption(id: string, caption: string | null): Promise<void> {
    const { error } = await chSupabase
      .from('ch_post_prints')
      .update({ caption })
      .eq('id', id);
    if (error) throw error;
  },

  async remove(id: string): Promise<void> {
    const { error } = await chSupabase
      .from('ch_post_prints')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },
};
